import { OperatingHoursSettings } from '../models/QRMenuSettings.model';

export const DAYS_OF_WEEK: (keyof OperatingHoursSettings)[] = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export interface OperatingStatusInfo {
  isOpen: boolean;
  messageKey: string;
  opensAtTime?: string;
  nextOpenDay?: string;
  closesAtTime?: string;
}

export function parseTimeToMinutes(timeStr?: string | null): number | null {
  if (!timeStr || typeof timeStr !== 'string') return null;
  const parts = timeStr.trim().split(':');
  if (parts.length < 2) return null;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (isNaN(h) || isNaN(m)) return null;
  return h * 60 + m;
}

/**
 * Checks whether an open/close time range represents being open 24 hours for the day.
 * Handles:
 * - "0:00" - "0:00", "00:00" - "00:00", or any identical open/close time
 * - "00:00" - "24:00", "0:00" - "24:00"
 * - "00:00" - "23:59", "0:00" - "23:59" (standard UI time pickers capping at 23:59)
 */
export function isTimeRange24Hours(openTime?: string | null, closeTime?: string | null): boolean {
  const o = parseTimeToMinutes(openTime);
  const c = parseTimeToMinutes(closeTime);
  if (o === null || c === null) return false;

  // 00:00 - 00:00, 0:00 - 0:00, or any openTime === closeTime
  if (o === c) return true;

  // 00:00 - 24:00 or 0:00 - 24:00
  if (o === 0 && c === 1440) return true;

  // 00:00 - 23:59 or 0:00 - 23:59
  if (o === 0 && c === 1439) return true;

  return false;
}

/**
 * Evaluates whether the restaurant is open based on UTC0 OperatingHours.
 * Handles overnight shifts where CloseTime < OpenTime and 24-hour schedules.
 */
export function evaluateOperatingHours(
  operatingHours?: OperatingHoursSettings | null,
  now: Date = new Date()
): OperatingStatusInfo {
  if (!operatingHours || Object.keys(operatingHours).length === 0) {
    return {
      isOpen: true,
      messageKey: 'OPERATING_HOURS.OPEN',
    };
  }

  // Check if all defined days have no schedules
  const hasAnySchedule = DAYS_OF_WEEK.some(day => operatingHours[day]?.IsOpen);
  if (!hasAnySchedule) {
    return {
      isOpen: true,
      messageKey: 'OPERATING_HOURS.OPEN',
    };
  }

  const currentUtcDay = now.getUTCDay(); // 0 = Sunday, 1 = Monday, ...
  const currentMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();

  const todayKey = DAYS_OF_WEEK[currentUtcDay];
  const yesterdayKey = DAYS_OF_WEEK[(currentUtcDay + 6) % 7];

  const todaySchedule = operatingHours[todayKey];
  const yesterdaySchedule = operatingHours[yesterdayKey];

  // 1. Check yesterday's overnight shift (e.g. Friday 18:00 to 02:00 UTC)
  // Note: 24h schedules on yesterday belong to yesterday and do not spill over as an overnight shift.
  if (yesterdaySchedule?.IsOpen && !isTimeRange24Hours(yesterdaySchedule.OpenTime, yesterdaySchedule.CloseTime)) {
    const yOpen = parseTimeToMinutes(yesterdaySchedule.OpenTime);
    const yClose = parseTimeToMinutes(yesterdaySchedule.CloseTime);
    if (yOpen !== null && yClose !== null && yClose < yOpen) {
      if (currentMinutes < yClose) {
        return {
          isOpen: true,
          messageKey: 'OPERATING_HOURS.OPEN',
          closesAtTime: yesterdaySchedule.CloseTime,
        };
      }
    }
  }

  // 2. Check today's shift
  if (todaySchedule?.IsOpen) {
    // 2a. Today is open 24 hours (e.g. 0:00-0:00, 00:00-00:00, 00:00-24:00, 00:00-23:59)
    if (isTimeRange24Hours(todaySchedule.OpenTime, todaySchedule.CloseTime)) {
      return {
        isOpen: true,
        messageKey: 'OPERATING_HOURS.OPEN',
        closesAtTime: todaySchedule.CloseTime,
      };
    }

    const tOpen = parseTimeToMinutes(todaySchedule.OpenTime);
    let tClose = parseTimeToMinutes(todaySchedule.CloseTime);

    if (tOpen !== null && tClose !== null) {
      // 23:59 close represents the end of the day (24:00 / midnight)
      if (tClose === 1439) {
        tClose = 1440;
      }

      if (tClose >= tOpen) {
        // Normal daytime shift (e.g. 09:00 - 22:00, or 09:00 - 24:00)
        if (currentMinutes >= tOpen && currentMinutes < tClose) {
          return {
            isOpen: true,
            messageKey: 'OPERATING_HOURS.OPEN',
            closesAtTime: todaySchedule.CloseTime,
          };
        }
      } else {
        // Overnight shift starting today (e.g. 18:00 - 02:00, or 18:00 - 00:00)
        if (currentMinutes >= tOpen) {
          return {
            isOpen: true,
            messageKey: 'OPERATING_HOURS.OPEN',
            closesAtTime: todaySchedule.CloseTime,
          };
        }
      }
    }
  }

  // 3. Restaurant is closed. Determine next opening time.
  // Check if opening later today
  if (todaySchedule?.IsOpen && !isTimeRange24Hours(todaySchedule.OpenTime, todaySchedule.CloseTime)) {
    const tOpen = parseTimeToMinutes(todaySchedule.OpenTime);
    if (tOpen !== null && currentMinutes < tOpen) {
      return {
        isOpen: false,
        messageKey: 'OPERATING_HOURS.OPENS_TODAY_AT',
        opensAtTime: todaySchedule.OpenTime,
      };
    }
  }

  // Look ahead in subsequent days
  for (let offset = 1; offset <= 7; offset++) {
    const nextDayIndex = (currentUtcDay + offset) % 7;
    const nextDayKey = DAYS_OF_WEEK[nextDayIndex];
    const nextSchedule = operatingHours[nextDayKey];
    if (nextSchedule?.IsOpen && nextSchedule.OpenTime) {
      if (offset === 1) {
        return {
          isOpen: false,
          messageKey: 'OPERATING_HOURS.OPENS_TOMORROW_AT',
          opensAtTime: nextSchedule.OpenTime,
          nextOpenDay: nextDayKey,
        };
      }
      return {
        isOpen: false,
        messageKey: 'OPERATING_HOURS.OPENS_DAY_AT',
        opensAtTime: nextSchedule.OpenTime,
        nextOpenDay: nextDayKey,
      };
    }
  }

  return {
    isOpen: false,
    messageKey: 'OPERATING_HOURS.CLOSED_TODAY',
  };
}
